import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import SellerAutocomplete from '../components/SellerAutocomplete';
import { useAuth } from '../contexts/AuthContext';

interface FormSubmission {
  _id: string;
  submissionId: string;
  annonsor: string;
  stillingstittel: string;
  arbeidsgiver: string;
  kampanjenavn: string;
  ordrenumre: string[];
  submittedAt: string;
  emailSent: boolean;
  asanaTaskCreated: boolean;
  pdfGenerated: boolean;
  userId: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    department?: string;
    newspaper?: string;
  };
  userSnapshot: {
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    department?: string;
    newspaper?: string;
  };
  // New error tracking fields
  integrationStatus?: {
    email: {
      sent: boolean;
      attempts: number;
      lastAttempt?: Date;
      error?: string;
      errorCode?: string;
    };
    asana: {
      created: boolean;
      taskId?: string;
      attempts: number;
      lastAttempt?: Date;
      error?: string;
      errorCode?: string;
    };
  };
  status?: 'success' | 'partial' | 'failed';
}

interface SubmissionsResponse {
  submissions: FormSubmission[];
  pagination: {
    current: number;
    total: number;
    limit: number;
    totalRecords: number;
  };
}

interface Stats {
  totalSubmissions: number;
  totalUsers: number;
  recentSubmissions: number;
  topAdvertisers: Array<{ _id: string; count: number }>;
  integrationStats: {
    totalSubmissions: number;
    emailsSent: number;
    asanaTasksCreated: number;
    pdfsGenerated: number;
  };
}

const AdminSubmissions: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<Stats | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    total: 1,
    limit: 20,
    totalRecords: 0
  });
  const [filters, setFilters] = useState({
    annonsor: '',
    stillingstittel: '',
    arbeidsgiver: '',
    seller: '',
    sellerId: '',
    fromDate: '',
    toDate: '',
    page: 1
  });

  const isAdmin = user?.role === 'administrator';

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: filters.page.toString(),
        limit: '20',
        ...(filters.annonsor && { annonsor: filters.annonsor }),
        ...(filters.stillingstittel && { stillingstittel: filters.stillingstittel }),
        ...(filters.arbeidsgiver && { arbeidsgiver: filters.arbeidsgiver }),
        ...(filters.sellerId && { userId: filters.sellerId }),
        ...(filters.fromDate && { fromDate: filters.fromDate }),
        ...(filters.toDate && { toDate: filters.toDate })
      });

      const apiUrl = `${process.env.REACT_APP_API_URL}/forms/submissions/all?${queryParams}`;
      console.log('Making request to:', apiUrl);
      const response = await fetch(apiUrl, {
        credentials: 'include'
      });
      console.log('Response status:', response.status, 'Content-Type:', response.headers.get('content-type'));

      if (response.ok) {
        const data: SubmissionsResponse = await response.json();
        console.log('AdminSubmissions: Fetched submissions data:', data);

        // Parse pagination values as numbers (backend may return strings)
        const parsedPagination = {
          current: parseInt(String(data.pagination.current)),
          total: parseInt(String(data.pagination.total)),
          limit: parseInt(String(data.pagination.limit)),
          totalRecords: parseInt(String(data.pagination.totalRecords))
        };

        setSubmissions(data.submissions);
        setPagination(parsedPagination);

        // If current page exceeds total pages, reset to page 1
        if (parsedPagination.current > parsedPagination.total && parsedPagination.total > 0) {
          console.warn(`Current page ${parsedPagination.current} exceeds total pages ${parsedPagination.total}, resetting to page 1`);
          setFilters(prev => ({ ...prev, page: 1 }));
        }
      } else {
        console.error('AdminSubmissions: Failed to fetch submissions, status:', response.status, response.statusText);
        const errorData = await response.text();
        console.error('AdminSubmissions: Error response:', errorData);
      }
    } catch (error) {
      console.error('Error fetching submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/forms/submissions/stats`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data: Stats = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchSubmissions();
      fetchStats();
    }
  }, [filters, isAdmin]);

  // Check admin access
  if (!isAdmin) {
    return (
      <div className="dashboard-page">
        <Header />
        <div className="dashboard-content">
          <div className="container">
            <h1>Ingen tilgang</h1>
            <p>Du har ikke tilgang til denne siden. Kun administratorer kan se skjemainnsendinger.</p>
          </div>
        </div>
      </div>
    );
  }

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1 // Reset to first page when filtering
    }));
  };

  const handlePageChange = (newPage: number) => {
    // Validate page bounds
    if (newPage < 1 || newPage > pagination.total) {
      console.warn(`Invalid page ${newPage}, must be between 1 and ${pagination.total}`);
      return;
    }
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const handleSellerChange = (value: string, seller?: any) => {
    setFilters(prev => ({
      ...prev,
      seller: value,
      sellerId: seller ? seller._id : '',
      page: 1
    }));
  };

  const clearFilters = () => {
    setFilters({
      annonsor: '',
      stillingstittel: '',
      arbeidsgiver: '',
      seller: '',
      sellerId: '',
      fromDate: '',
      toDate: '',
      page: 1
    });
  };

  const viewSubmission = (submissionId: string) => {
    navigate(`/dashboard/submissions/${submissionId}`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('no-NO');
  };

  const retryEmail = async (submissionId: string) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/forms/submissions/${submissionId}/retry-email`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        alert('E-post sendt!');
        fetchSubmissions(); // Refresh list
      } else {
        const error = await response.json();
        alert(`Feil: ${error.message || 'Kunne ikke sende e-post'}`);
      }
    } catch (error) {
      console.error('Error retrying email:', error);
      alert('Det oppstod en feil ved sending av e-post');
    }
  };

  const retryAsana = async (submissionId: string) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/forms/submissions/${submissionId}/retry-asana`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        alert('Asana-oppgave opprettet!');
        fetchSubmissions(); // Refresh list
      } else {
        const error = await response.json();
        alert(`Feil: ${error.message || 'Kunne ikke opprette Asana-oppgave'}`);
      }
    } catch (error) {
      console.error('Error retrying Asana:', error);
      alert('Det oppstod en feil ved oppretting av Asana-oppgave');
    }
  };

  return (
    <div className="dashboard-page">
      <Header />
      <div className="dashboard-content">
        <div className="container">
          <div className="page-header">
            <h1>Skjemainnsendinger</h1>
            <button
              className="btn btn-secondary"
              onClick={() => navigate('/dashboard')}
            >
              ← Tilbake til dashboard
            </button>
          </div>

          {/* Statistics */}
          {stats && (
            <div className="stats-grid">
              <div className="stat-card">
                <h3>{stats.totalSubmissions}</h3>
                <p>Totale innsendinger</p>
              </div>
              <div className="stat-card">
                <h3>{stats.totalUsers}</h3>
                <p>Aktive brukere</p>
              </div>
              <div className="stat-card">
                <h3>{stats.recentSubmissions}</h3>
                <p>Siste 30 dager</p>
              </div>
              <div className="stat-card">
                <h3>{Math.round((stats.integrationStats.emailsSent / stats.integrationStats.totalSubmissions) * 100)}%</h3>
                <p>E-post suksessrate</p>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="filters-section">
            <h3>Filtrer innsendinger</h3>
            <div className="filters-grid">
              <div className="filter-group">
                <label htmlFor="annonsor">Annonsør</label>
                <input
                  type="text"
                  id="annonsor"
                  value={filters.annonsor}
                  onChange={(e) => handleFilterChange('annonsor', e.target.value)}
                  placeholder="Søk etter annonsør..."
                />
              </div>
              <div className="filter-group">
                <label htmlFor="stillingstittel">Stillingstittel</label>
                <input
                  type="text"
                  id="stillingstittel"
                  value={filters.stillingstittel}
                  onChange={(e) => handleFilterChange('stillingstittel', e.target.value)}
                  placeholder="Søk etter stillingstittel..."
                />
              </div>
              <div className="filter-group">
                <label htmlFor="arbeidsgiver">Arbeidsgiver</label>
                <input
                  type="text"
                  id="arbeidsgiver"
                  value={filters.arbeidsgiver}
                  onChange={(e) => handleFilterChange('arbeidsgiver', e.target.value)}
                  placeholder="Søk etter arbeidsgiver..."
                />
              </div>
              <div className="filter-group">
                <label htmlFor="seller">Selger</label>
                <SellerAutocomplete
                  value={filters.seller}
                  onChange={handleSellerChange}
                  placeholder="Søk etter selger..."
                />
              </div>
              <div className="filter-group">
                <label htmlFor="fromDate">Fra dato</label>
                <input
                  type="date"
                  id="fromDate"
                  value={filters.fromDate}
                  onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                />
              </div>
              <div className="filter-group">
                <label htmlFor="toDate">Til dato</label>
                <input
                  type="date"
                  id="toDate"
                  value={filters.toDate}
                  onChange={(e) => handleFilterChange('toDate', e.target.value)}
                />
              </div>
              <div className="filter-actions">
                <button
                  className="btn btn-secondary"
                  onClick={clearFilters}
                >
                  Nullstill filtre
                </button>
              </div>
            </div>
          </div>

          {/* Submissions Table */}
          <div className="submissions-section">
            <div className="section-header">
              <h3>Innsendinger ({pagination.totalRecords})</h3>
            </div>

            {loading ? (
              <div className="loading">Laster innsendinger...</div>
            ) : submissions.length === 0 ? (
              <div className="no-data">Ingen innsendinger funnet</div>
            ) : (
              <>
                <div className="submissions-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Dato</th>
                        <th>Kampanjenavn</th>
                        <th>Annonsør</th>
                        <th>Arbeidsgiver</th>
                        <th>Innsender</th>
                        <th>Status</th>
                        <th>Handlinger</th>
                      </tr>
                    </thead>
                    <tbody>
                      {submissions.map((submission) => (
                        <tr key={submission._id}>
                          <td>{formatDate(submission.submittedAt)}</td>
                          <td>{submission.kampanjenavn}</td>
                          <td>{submission.annonsor}</td>
                          <td>{submission.arbeidsgiver}</td>
                          <td>
                            {submission.userId ?
                              `${submission.userId.firstName} ${submission.userId.lastName}` :
                              `${submission.userSnapshot.firstName} ${submission.userSnapshot.lastName}`
                            }
                            <br />
                            <small>
                              {submission.userId?.department || submission.userSnapshot.department}
                            </small>
                          </td>
                          <td>
                            <div className="status-indicators">
                              <div className="status-row">
                                <span className={`status-badge ${submission.emailSent ? 'success' : 'error'}`}>
                                  {submission.emailSent ? '✓' : '✗'} E-post
                                </span>
                                {!submission.emailSent && (
                                  <button
                                    className="btn btn-tiny btn-warning"
                                    onClick={() => retryEmail(submission._id)}
                                    title="Send e-post på nytt"
                                  >
                                    ↻ Send på nytt
                                  </button>
                                )}
                              </div>
                              <div className="status-row">
                                <span className={`status-badge ${submission.asanaTaskCreated ? 'success' : 'error'}`}>
                                  {submission.asanaTaskCreated ? '✓' : '✗'} Asana
                                </span>
                                {!submission.asanaTaskCreated && (
                                  <button
                                    className="btn btn-tiny btn-warning"
                                    onClick={() => retryAsana(submission._id)}
                                    title="Opprett Asana-oppgave på nytt"
                                  >
                                    ↻ Send på nytt
                                  </button>
                                )}
                              </div>
                              <span className={`status-badge ${submission.pdfGenerated ? 'success' : 'error'}`}>
                                {submission.pdfGenerated ? '✓' : '✗'} PDF
                              </span>
                            </div>
                          </td>
                          <td>
                            <button
                              className="btn btn-small btn-primary"
                              onClick={() => viewSubmission(submission._id)}
                            >
                              Se detaljer
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pagination.total > 1 && (
                  <div className="pagination">
                    <button
                      className="btn btn-secondary"
                      disabled={pagination.current === 1}
                      onClick={() => handlePageChange(pagination.current - 1)}
                    >
                      Forrige
                    </button>
                    <span className="pagination-info">
                      Side {pagination.current} av {pagination.total}
                    </span>
                    <button
                      className="btn btn-secondary"
                      disabled={pagination.current === pagination.total}
                      onClick={() => handlePageChange(pagination.current + 1)}
                    >
                      Neste
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSubmissions;